import React, { useCallback, useMemo } from 'react';

import { useTranslation } from '@/i18n';
import { colors } from '@/assets/styles/colors';
import { ConfirmModal } from '@/components/ui/modals/ConfirmModal';
import { Text } from '@/components/ui/text/Text';
import { TextArea } from '@/components/ui/inputs';
import { SimpleScrollTable } from '@/components/ui/tables';

import type {
  IncidentManagementItem,
  LC_BulkApproveIncidentsOut,
} from '@/api/domains/employeeIncident/types';
import { INCIDENT_BULK_APPROVER_COMMENT_MAX_LENGTH } from '@/api/domains/employeeIncident/types';
import { useIncidentBulkApproval } from '@/hooks/attendance/useIncidentBulkApproval';
import {
  useIncidentBulkApprovalModalList,
  type IncidentBulkApprovalModalFilters,
} from '@/hooks/attendance/useIncidentBulkApprovalModalList';


interface IncidentBulkApprovalModalProps {
  isOpen: boolean;
  selectedIncidents: IncidentManagementItem[];
  onClose: () => void;
  onSuccess: (result: LC_BulkApproveIncidentsOut) => void;
  /**
   * Modo "seleccionar todo" (ASIS-5249): cuando es `true`, `selectedIncidents` (página visible)
   * se ignora para el listado del modal — se muestra en su lugar un scroll infinito de todas
   * las incidencias Pendientes que cumplen `filters`, y la aprobación real la resuelve el
   * backend con esos mismos filtros al momento de ejecutar.
   */
  isSelectAllMode?: boolean;
  /** Total de incidencias Pendientes que cumplen los filtros vigentes (KPI "Pendientes"). */
  selectAllTotalCount?: number;
  filters?: IncidentBulkApprovalModalFilters;
  /** IDs deseleccionados manualmente en modo "seleccionar todo" (de la página visible). */
  excludeIncidentIds?: number[];
}

export const IncidentBulkApprovalModal: React.FC<IncidentBulkApprovalModalProps> = ({
  isOpen,
  selectedIncidents,
  onClose,
  onSuccess,
  isSelectAllMode = false,
  selectAllTotalCount = 0,
  filters,
  excludeIncidentIds = [],
}) => {
  const { t: translate } = useTranslation();

  const selectedIncidentIds = useMemo(
    () => selectedIncidents.map((incident) => incident.incidentId),
    [selectedIncidents],
  );

  const effectiveFilters = useMemo(
    () => ({ ...filters, excludeIncidentIds }),
    [filters, excludeIncidentIds],
  );

  const { comment, setComment, canSubmit, isSubmitting, handleSubmit } = useIncidentBulkApproval({
    selectedIncidentIds,
    onSuccess,
    isSelectAllMode,
    filters: effectiveFilters,
  });

  const { allItems, isLoadingMore, scrollContainerRef } = useIncidentBulkApprovalModalList({
    isSelectAllMode,
    isOpen,
    filters: effectiveFilters,
  });

  // El modal nunca se desmonta (isOpen solo controla el `return null` de más abajo), así que
  // `comment` sobrevive entre aperturas si no se limpia explícitamente al cerrar — sin esto, un
  // comentario escrito y luego cancelado reaparece precargado al reabrir (riesgo de integridad
  // en un campo que forma parte del registro de auditoría).
  const handleClose = useCallback(() => {
    setComment('');
    onClose();
  }, [setComment, onClose]);

  const displayItems = isSelectAllMode ? allItems : selectedIncidents;
  const displayCount = isSelectAllMode
    ? Math.max(selectAllTotalCount - excludeIncidentIds.length, 0)
    : selectedIncidents.length;

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="incident-bulk-approval-modal-backdrop"
    >
      <ConfirmModal
        variant="success"
        icon="bell"
        iconColor={colors.feedback.alert300}
        iconSize={24}
        title={translate('attendance:incidentManagement.bulk.approveTitle')}
        bodyText={translate('attendance:incidentManagement.bulk.approveBodyText')}
        bodyTextColor={colors.grays.neutral00}
        onConfirm={handleSubmit}
        onCancel={handleClose}
        onClose={handleClose}
        confirmText={translate('attendance:incidentManagement.bulk.approve')}
        cancelText={translate('attendance:incidentManagement.bulk.cancel')}
        isLoading={isSubmitting}
        isConfirmDisabled={!canSubmit}
        cardClassName="w-full max-w-[876px] h-[750px]"
        buttonTextVariant="subheader-medium"
        confirmButtonTextColor={colors.grays.neutralFF}
      >
        <div className="flex gap-6">
          {/* Rectángulo izquierdo: Solicitudes seleccionadas — 402x427px */}
          <div
            className="flex flex-col gap-2 rounded-2xl p-4 overflow-hidden shrink-0"
            style={{ width: 402, height: 427, border: `1px solid ${colors.primary.auxiliar}` }}
          >
            <Text variant="body-medium" color={colors.primary.dash}>
              {translate('attendance:incidentManagement.bulk.selectedCount', {
                count: displayCount,
              })}
            </Text>
            <SimpleScrollTable
              columns={[
                {
                  key: 'incidentId',
                  header: translate('attendance:incidentManagement.bulk.table.id'),
                  render: (incident: IncidentManagementItem) => incident.incidentId,
                },
                {
                  key: 'employeeName',
                  header: translate('attendance:incidentManagement.bulk.table.employee'),
                  render: (incident: IncidentManagementItem) => incident.employeeName,
                },
              ]}
              data={displayItems}
              getRowId={(incident) => incident.incidentId}
              isLoadingMore={isLoadingMore}
              scrollContainerRef={scrollContainerRef}
              className="flex-1"
              data-testid="incident-bulk-approval-list"
            />
          </div>

          {/* Rectángulo derecho: Ingresar comentario (Obligatorio) — 402x427px */}
          <div
            className="flex flex-col gap-2 rounded-2xl p-4 shrink-0"
            style={{ width: 402, height: 427, border: `1px solid ${colors.primary.auxiliar}` }}
          >
            <Text variant="body-medium" color={colors.primary.dash}>
              {translate('attendance:incidentManagement.bulk.commentLabel')}
            </Text>
            <TextArea
              value={comment}
              onChange={setComment}
              placeholder={translate('attendance:incidentManagement.bulk.commentPlaceholder')}
              maxLength={INCIDENT_BULK_APPROVER_COMMENT_MAX_LENGTH}
              showCharacterCount
              fullWidth={false}
              inputClassName="w-[354px] h-[124px] !rounded-[16px] placeholder:text-[#999999] placeholder:text-[14px] placeholder:font-roboto"
              customColors={{
                border: colors.primary.background,
                background: colors.primary.background,
              }}
              data-testid="incident-bulk-approval-comment-input"
            />
          </div>
        </div>
      </ConfirmModal>
    </div>
  );
};

export default IncidentBulkApprovalModal;
